<template>
    <!-- Date Range Picker -->
    <v-col cols="12" sm="4" md="4" lg="3">
        <v-date-input
            ref="dateField"
            v-model="modelValue.date"
            :label="$t('formFields.date')"
            variant="outlined"
            multiple="range"
            prepend-icon=""
            @update:modelValue="updateDetails('date', $event)"
            :rules="[requiredRule]"
        ></v-date-input>
    </v-col>

    <!-- Time Range Picker -->
    <v-col cols="12" sm="4" md="4" lg="3">
        <v-menu v-model="timePicker" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
                <v-text-field
                    v-model="formattedTime"
                    :label="$t('formFields.timeInterval')"
                    readonly
                    v-bind="props"
                    hide-details="auto"
                />
            </template>
            <v-card>
                <v-container>
                    <v-row>
                        <v-col>
                            <v-time-picker
                                v-model="modelValue.timeStart"
                                format="24hr"
                                scrollable
                                :title="$t('formFields.selectStartTime')"
                                @update:modelValue="
                                    updateTime('timeStart', $event)
                                "
                            />
                        </v-col>
                        <v-col>
                            <v-time-picker
                                v-model="modelValue.timeEnd"
                                format="24hr"
                                scrollable
                                :title="$t('formFields.selectEndTime')"
                                @update:modelValue="
                                    updateTime('timeEnd', $event)
                                "
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card>
        </v-menu>
    </v-col>
</template>

<script>
export default {
    /**
     * Component properties
     * @property {Object} modelValue - The v-model object containing date and time details.
     * @property {String} formType - The form type (e.g., "truck" or "ware").
     */
    props: {
        modelValue: Object,
        formType: String,
    },

    /**
     * Events emitted by the component
     * @event update:modelValue - Emits updated date and time details.
     */
    emits: ["update:modelValue"],
    data() {
        return {
            datePicker: false,
            timePicker: false,
            requiredRule: (value) => !!value || this.$t("validation.required"),
        };
    },
    computed: {
        /**
         * Computed property to format the selected time interval as a string.
         * @returns {string} - Formatted time interval (e.g., "08:00 - 18:00").
         */
        formattedTime() {
            return this.modelValue.timeStart && this.modelValue.timeEnd
                ? `${this.modelValue.timeStart} - ${this.modelValue.timeEnd}`
                : "";
        },
    },
    methods: {
        /**
         * Manually triggers validation on all location fields.
         * Returns true if all fields are valid, false otherwise.
         */
        async validateFields() {
            await this.$nextTick(); // Ensure reactivity updates before validation

            let isValid = true;

            // Validate FormDateTime
            // if (this.$refs.dateTime) {
            //   const dateTimeValid = await this.$refs.dateTime.validateFields();
            //
            //   if (!dateTimeValid) isValid = false;
            // }

            // Validate FormDateField
            if (
                !this.modelValue.date ||
                !Array.isArray(this.modelValue.date) ||
                this.modelValue.date.length === 0
            ) {
                isValid = false;
            }

            return isValid;
        },
        resetValidation() {
            this.$nextTick(() => {
                if (this.$refs.dateTime?.resetValidation) {
                    this.$refs.dateTime.resetValidation();
                }
                if (this.$refs.dateField?.resetValidation) {
                    this.$refs.dateField.resetValidation();
                }
            });
        },
        /**
         * Updates the specified field in localWareDetails and emits the updated object.
         * @param {string} field - The field to update (e.g., "date").
         * @param {any} value - The new value for the field.
         */

        updateDetails(field, value) {
            if (field === "date" && Array.isArray(value) && value.length > 0) {
                const ymd = (v) => {
                    const d = v instanceof Date ? v : new Date(v);
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    return `${y}-${m}-${day}`; // no UTC conversion, no shift
                };

                const startDate = value[0];
                const endDate = value[value.length - 1];

                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    dateStart: ymd(startDate),
                    dateEnd: ymd(endDate),
                    date: value,
                });
            } else {
                this.$emit("update:modelValue", {
                    ...this.modelValue,
                    [field]: value,
                });
            }
        },
        /**
         * Updates the time-related fields and closes the time picker if both values are selected.
         * @param {string} field - The time field to update ("timeStart" or "timeEnd").
         * @param {string} value - The selected time value (e.g., "08:00").
         */
        updateTime(field, value) {
            this.$emit("update:modelValue", {
                ...this.modelValue,
                [field]: value,
            });
            if (this.modelValue.timeStart && this.modelValue.timeEnd) {
                this.$nextTick(() => {
                    this.timePicker = false;
                });
            }
        },
    },
};
</script>
